import * as React from "react";
import { observer } from "mobx-react";
import { RecentlyBookedHotelsProps } from "./typings";
import { UitkCardLink, UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkRating } from "uitk-react-rating";
import { LocalizedText } from "bernie-l10n";
import { TrackedLink } from "src/components/utility/analytics/TrackedLink";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { withStores } from "stores";
import { RecentlyBookedHotelsFlexModuleResult } from "typings/microserviceModels/recently-booked-hotels-flex-module";
import { UitkSpacing } from "uitk-react-spacing";

export const RecentlyBookedHotels = withStores("flexModuleModelStore")(
  observer((props: RecentlyBookedHotelsProps) => {
    const { templateComponent, flexModuleModelStore } = props;
    const {
      metadata: { id },
      config: { fmId, fmTitleId },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as RecentlyBookedHotelsFlexModuleResult | null;
    /* istanbul ignore next */
    if (!model) {
      return null;
    }

    const items = (model && model.items && model.items.filter((item) => item)) || [];
    const { formatText } = useLocalization();

    if (!model || items.length === 0) {
      return null;
    }
    const { title } = model;

    return (
      <Viewport>
        <ViewMedium>
          <UitkSpacing padding={{ block: "three" }}>
            <div className="RecentlyBookedHotels" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
              <div className="carouselMediumContainer">
                <UitkHeading tag="h2" size={4}>
                  {title}
                </UitkHeading>
                <UitkSpacing padding={{ blockstart: "two" }}>
                  <IsomorphicCarousel
                    itemsVisible={{
                      lg: 2,
                      md: 1,
                      sm: 1,
                    }}
                    buttonText={{
                      nextButton: formatText("carousel.item.next"),
                      prevButton: formatText("carousel.item.prev"),
                    }}
                    pageBy={1}
                    peek
                  >
                    {items.map((recentlyBookedHotel, index) => {
                      return (
                        <div key={recentlyBookedHotel.name}>
                          <RecentlyBookedHotelCard
                            hotelName={recentlyBookedHotel.name}
                            hisUrl={recentlyBookedHotel.url}
                            isMobile={false}
                            starRating={recentlyBookedHotel.rating}
                            lastBookedMessage={formatLastBookedMessage(
                              recentlyBookedHotel.bookedAtNumber,
                              recentlyBookedHotel.bookedAtKey,
                              formatText
                            )}
                            cardIndex={index}
                            imageUrl={recentlyBookedHotel.hotelImageUrl}
                          />
                        </div>
                      );
                    })}
                  </IsomorphicCarousel>
                </UitkSpacing>
              </div>
            </div>
          </UitkSpacing>
        </ViewMedium>
        <ViewSmall>
          <UitkSpacing padding={{ block: "three" }}>
            <div className="RecentlyBookedHotels" id={id} data-fm={fmId} data-fm-title-id={fmTitleId}>
              <UitkSpacing margin={{ blockend: "three" }}>
                <div>
                  <UitkHeading tag="h2" size={4}>
                    {title}
                  </UitkHeading>
                </div>
              </UitkSpacing>

              <UitkLayoutGrid space="three" columns={1}>
                {items.map((recentlyBookedHotel, index) => {
                  return (
                    <UitkLayoutGridItem colSpan={1} key={recentlyBookedHotel.name}>
                      <RecentlyBookedHotelCard
                        hotelName={recentlyBookedHotel.name}
                        hisUrl={recentlyBookedHotel.url}
                        isMobile
                        starRating={recentlyBookedHotel.rating}
                        lastBookedMessage={formatLastBookedMessage(
                          recentlyBookedHotel.bookedAtNumber,
                          recentlyBookedHotel.bookedAtKey,
                          formatText
                        )}
                        cardIndex={index}
                      />
                    </UitkLayoutGridItem>
                  );
                })}
              </UitkLayoutGrid>
            </div>
          </UitkSpacing>
        </ViewSmall>
      </Viewport>
    );
  })
);

interface RecentlyBookedHotelsCardProps {
  hotelName: string;
  hisUrl: string;
  isMobile: boolean;
  starRating: string;
  lastBookedMessage: string;
  cardIndex: number;
  imageUrl?: string;
}

const RecentlyBookedHotelCard = (props: RecentlyBookedHotelsCardProps) => {
  return (
    <UitkCard border={!props.isMobile} className="RecentlyBookedHotelsCard">
      <UitkLayoutFlex>
        {!props.isMobile && (
          <UitkLayoutFlexItem minWidth={150} shrink={0}>
            <UitkCardContentSection padded={false}>
              <UitkFigure className="all-image-fit-cover" ratio={UitkFigure.AspectRatio.R4_3}>
                <UitkImage src={props.imageUrl} alt={props.hotelName} placeholderImage lazyLoading="lazy" />
              </UitkFigure>
            </UitkCardContentSection>
          </UitkLayoutFlexItem>
        )}
        <UitkLayoutFlexItem>
          <UitkLayoutFlex direction="column" justifyContent="space-between">
            <UitkCardContentSection>
              <UitkHeading overflow="truncate-2-lines" tag="h3" size={5}>
                {props.hotelName}
              </UitkHeading>
              <UitkRating rating={props.starRating} />
            </UitkCardContentSection>
            <UitkCardContentSection>
              <UitkText inline size={200}>
                {props.lastBookedMessage}
              </UitkText>
            </UitkCardContentSection>
            <UitkCardLink>
              <TrackedLink moduleName="recently-booked-hotels" href={props.hisUrl} rfrr={`card-${props.cardIndex}`}>
                <LocalizedText message="hotels.hotel.open.page" data={[props.hotelName]} />
              </TrackedLink>
            </UitkCardLink>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </UitkCard>
  );
};

const formatLastBookedMessage = (
  lastBookedNumber: number,
  lastBookedLocKey: string,
  formatText: (key: string, ...args: any[]) => string
): string => {
  let locKey = "";
  switch (lastBookedLocKey) {
    case "hotels.hotel.listing.minute":
    case "hotels.hotel.listing.minutes": {
      locKey = "recentlyBookedHotels.lastBooking.minutes";
      break;
    }
    case "hotels.hotel.listing.hour":
    case "hotels.hotel.listing.hours": {
      locKey = "recentlyBookedHotels.lastBooking.hours";
      break;
    }
  }

  return formatText(locKey, lastBookedNumber);
};

export default RecentlyBookedHotels;
